/**
 * `feedHold` — which COMMS entries must not be rendered yet, because their own dice are still in
 * the air.
 *
 * Without this, the result card appears the instant the resolution broadcast arrives, so the
 * player reads the damage number and only afterwards watches the dice land. The card is the
 * *earliest* disclosure of the outcome, because it needs no animation to run first.
 *
 * **Only roll outcomes hold, and only their own**: a teammate's typed chat line still appears
 * instantly while dice are airborne — whole-feed holding is deliberately not the rule. Ad-hoc
 * `/roll` is in scope alongside combat resolutions, so a `/roll` card doesn't spoil its own dice
 * one layer down.
 *
 * Three release valves, and the design intent is that the first two make the third unreachable:
 *
 *  1. **Backfill and scrollback never hold.** An entry at or below the high-water mark is history —
 *     a reload's `game_welcome.feed` tail, or a reconnect re-delivering it — and its dice were
 *     thrown (or missed) long ago. This is the `feedBaselineTs`/{@link ./feedFreshness} rule
 *     verbatim, including the trap it exists for: seeding the baseline from the subscriber's own
 *     first observation replays the most recent roll on every page load, because the backfill
 *     arrives asynchronously AFTER mount.
 *  2. **An entry with no dice never holds.** Computed from the entry's own recorded steps/draws, so
 *     it is a fact about the card rather than a guess about the dice: a pure move or status
 *     application, or a resolution whose steps rolled nothing, renders with no delay at all. So
 *     does a resolution card when COMBAT.app is closed — nothing is mounted that could throw for
 *     it, and the card must not wait for dice that are never coming.
 *  3. **The ceiling underneath everything** ({@link ./throwSettlement}), which turns any hold whose
 *     throw never registers into a late card rather than a lost one.
 */
import type { FeedEntry } from "@lancer-vtt/shared";
import { diceSpecsFromFeedRoll } from "./feedRollDice";
import { diceSpecsFromSteps } from "./resolutionDice";
import type { ResolvedStep } from "@lancer-vtt/domain";

/** One card to withhold: which feed entry to hide, and which throw's settle releases it. */
export type FeedHold = {
  /** The feed entry's id — what the render seam filters on. */
  entryId: string;
  /**
   * The key the dice throw was published under. For a combat resolution that's the SERVER-stamped
   * `resolution.id`, which the encounter socket stamped on the same resolution's dice throw; for an
   * ad-hoc `/roll` the card and the dice ARE the same entry, so its own id is already the key.
   */
  throwKey: string;
};

export type FeedHoldResult = {
  holds: FeedHold[];
  /** `max(highWater, ...feed.map(e => e.ts))` — monotonically non-decreasing, as in `selectFreshRolls`. */
  nextHighWater: number;
};

/**
 * Pick the live entries whose reveal must wait for their own dice.
 *
 * `combatSourceMounted` answers "could a combat-resolution throw even happen right now?" — see
 * {@link ./resolutionThrows.hasResolutionThrowSource}. It gates ONLY resolution cards: an ad-hoc
 * `/roll` is thrown by `DiceFeedSubscriber`, a peer of COMMS.app itself, so its dice are always on
 * their way.
 */
export function selectFeedHolds(feed: readonly FeedEntry[], highWater: number, combatSourceMounted: boolean): FeedHoldResult {
  const holds: FeedHold[] = [];
  let nextHighWater = highWater;
  for (const entry of feed) {
    if (entry.ts > nextHighWater) nextHighWater = entry.ts;
    if (entry.ts <= highWater) continue; // valve 1: backfill / scrollback renders at once
    if (entry.kind === "roll") {
      if (diceSpecsFromFeedRoll(entry).length > 0) holds.push({ entryId: entry.id, throwKey: entry.id });
      continue;
    }
    if (entry.kind !== "system" || entry.event !== "resolution" || entry.resolution === undefined) continue;
    if (!combatSourceMounted) continue; // valve 2: COMBAT.app closed — no throw is coming
    if (diceSpecsFromSteps(entry.resolution.steps as ResolvedStep[]).length === 0) continue; // valve 2: rolled nothing
    holds.push({ entryId: entry.id, throwKey: entry.resolution.id });
  }
  return { holds, nextHighWater };
}
